import { countryName } from '../../../utils/phone'

// ---------------------------------------------------------------------------
// Telephony onboarding — static option sets + form metadata.
//
// Dropdown option VALUES are verbatim from the two form specs:
//   .references/rolodex-backend/dialer_verification.md      (Appendix A)
//   .references/rolodex-backend/dialer_voice_integrity.md   (Appendix A)
// Rolodex maps each value to Twilio's exact enum string internally, so these
// must not drift. Display strings follow the specs' "Display" columns.
// ---------------------------------------------------------------------------

export interface Option {
  value: string
  label: string
}

// ── Business verification (dialer_verification.md) ────────────────────────

// A.1 businessIdentity — "Direct customer" is set for the customer and never
// changed (renders as a locked field per design 1c).
export const BUSINESS_IDENTITY_OPTIONS: Option[] = [
  { value: 'DirectCustomer', label: 'Direct customer' },
  { value: 'IsvResellerOrPartner', label: 'ISV / reseller / partner' },
]

// A.2 businessType
export const BUSINESS_TYPE_OPTIONS: Option[] = [
  { value: 'SoleProprietorship', label: 'Sole Proprietorship' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'LimitedLiabilityCorporation', label: 'Limited Liability Corporation (LLC)' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'CoOperative', label: 'Co-operative' },
  { value: 'LimitedLiabilityPartnership', label: 'Limited Liability Partnership (LLP)' },
  { value: 'NonProfit', label: 'Non-profit' },
]

// A.3 industry — display in Title Case; submit the value verbatim.
export const INDUSTRY_OPTIONS: Option[] = [
  { value: 'Agriculture', label: 'Agriculture' },
  { value: 'Automotive', label: 'Automotive' },
  { value: 'Banking', label: 'Banking' },
  { value: 'Consumer', label: 'Consumer' },
  { value: 'Education', label: 'Education' },
  { value: 'Electronics', label: 'Electronics' },
  { value: 'Energy', label: 'Energy' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'FastMovingConsumerGoods', label: 'Fast Moving Consumer Goods' },
  { value: 'Financial', label: 'Financial' },
  { value: 'Fintech', label: 'Fintech' },
  { value: 'FoodAndBeverage', label: 'Food & Beverage' },
  { value: 'Government', label: 'Government' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Hospitality', label: 'Hospitality' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Jewelry', label: 'Jewelry' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Media', label: 'Media' },
  { value: 'NotForProfit', label: 'Not For Profit' },
  { value: 'OilAndGas', label: 'Oil & Gas' },
  { value: 'Online', label: 'Online' },
  { value: 'RawMaterials', label: 'Raw Materials' },
  { value: 'RealEstate', label: 'Real Estate' },
  { value: 'Religion', label: 'Religion' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Telecommunications', label: 'Telecommunications' },
  { value: 'Transportation', label: 'Transportation' },
  { value: 'Travel', label: 'Travel' },
]

// A.4 registrationIdentifierType
export const REGISTRATION_ID_TYPE_OPTIONS: Option[] = [
  { value: 'EIN', label: 'EIN — US Employer Identification Number' },
  { value: 'DUNS', label: 'DUNS — Dun & Bradstreet Number' },
]

// A.5 regionOfOperation — default UsaAndCanada.
export const REGION_OF_OPERATION_OPTIONS: Option[] = [
  { value: 'UsaAndCanada', label: 'USA & Canada' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Asia', label: 'Asia' },
  { value: 'Africa', label: 'Africa' },
  { value: 'LatinAmerica', label: 'Latin America' },
]

// A.6 jobPosition
export const JOB_POSITION_OPTIONS: Option[] = [
  { value: 'Director', label: 'Director' },
  { value: 'GM', label: 'General Manager' },
  { value: 'VP', label: 'Vice President' },
  { value: 'CEO', label: 'CEO' },
  { value: 'CFO', label: 'CFO' },
  { value: 'GeneralCounsel', label: 'General Counsel' },
]

// A.7 country (address + region-of-operation scope) — US & CA only.
export const COUNTRY_OPTIONS: Option[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
]

// ── US states / CA provinces (two-letter codes) ───────────────────────────

export const US_STATES: Option[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

export const CA_PROVINCES: Option[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
]

// State/province options filtered by the chosen address country.
export function statesForCountry(country: string): Option[] {
  return country === 'CA' ? CA_PROVINCES : US_STATES
}

// ── Recording exclusion regions ─────────────────────────────────────────────
// The guardrails "Don't record calls to" picker + chips operate on these codes;
// the projection / PUT payload use the same form. US & Canada are per state or
// province (prefixed ISO-3166-2, "US-FL"); any other country is one bare ISO
// code ("DE"), offered from the tenant's dialable destinations.

export interface RegionOption {
  code: string // "US-FL" | "CA-QC" | "DE"
  name: string // "Florida" | "Quebec" | "Germany"
  country: 'US' | 'CA' | 'other'
}

export const RECORDING_REGION_OPTIONS: RegionOption[] = [
  ...US_STATES.map((s) => ({ code: `US-${s.value}`, name: s.label, country: 'US' as const })),
  ...CA_PROVINCES.map((p) => ({ code: `CA-${p.value}`, name: p.label, country: 'CA' as const })),
]

const _REGION_LABELS: Record<string, string> = Object.fromEntries(
  RECORDING_REGION_OPTIONS.map((r) => [r.code, r.name]),
)

// Whole-country choices outside US & Canada, named like the rest of the dialer.
export function recordingCountryOptions(codes: string[]): RegionOption[] {
  return codes.map((code) => ({ code, name: countryName(code), country: 'other' as const }))
}

// "US-FL" → "Florida", "DE" → "Germany"; unknown → the raw code (best effort).
export function regionLabel(code: string): string {
  return _REGION_LABELS[code] || (code.includes('-') ? code : countryName(code))
}

// ── Voice integrity (dialer_voice_integrity.md, Appendix A) ~45 use cases ──

export const VOICE_INTEGRITY_USE_CASE_OPTIONS: Option[] = [
  { value: 'IdentifyAndVerification', label: 'Identify & Verification' },
  { value: 'AssetManagement', label: 'Asset Management' },
  { value: 'LeadGeneration', label: 'Lead Generation' },
  { value: 'IntelligentRouting', label: 'Intelligent Routing' },
  { value: 'AppointmentScheduling', label: 'Appointment Scheduling' },
  { value: 'CustomerSupport', label: 'Customer Support' },
  { value: 'SelfService', label: 'Self-Service' },
  { value: 'AutomatedSupport', label: 'Automated Support' },
  { value: 'AppointmentReminders', label: 'Appointment Reminders' },
  { value: 'EmployeeNotifications', label: 'Employee Notifications' },
  { value: 'DeliveryNotifications', label: 'Delivery Notifications' },
  { value: 'EmergencyNotifications', label: 'Emergency Notifications' },
  { value: 'ContactlessDelivery', label: 'Contactless Delivery' },
  { value: 'OrderNotifications', label: 'Order Notifications' },
  { value: 'ServiceAlerts', label: 'Service Alerts' },
  { value: 'PurchaseConfirmation', label: 'Purchase Confirmation' },
  { value: 'MassAlerts', label: 'Mass Alerts' },
  { value: 'FraudAlerts', label: 'Fraud Alerts' },
  { value: 'ContactTracing', label: 'Contact Tracing' },
  { value: 'LeadManagement', label: 'Lead Management' },
  { value: 'LeadNurturing', label: 'Lead Nurturing' },
  { value: 'Telemarketing', label: 'Telemarketing' },
  { value: 'MarketingEvents', label: 'Marketing Events' },
  { value: 'RewardsProgram', label: 'Rewards Program' },
  { value: 'LeadAlerts', label: 'Lead Alerts' },
  { value: 'LeadDistribution', label: 'Lead Distribution' },
  { value: 'AbandonedCart', label: 'Abandoned Cart' },
  { value: 'CallTracking', label: 'Call Tracking' },
  { value: 'OutboundDialer', label: 'Outbound Dialer' },
  { value: 'ClickToCall', label: 'Click to Call' },
  { value: 'PhoneSystem', label: 'Phone System' },
  { value: 'MeetingsCollaboration', label: 'Meetings/Collaboration' },
  { value: 'Telehealth', label: 'Telehealth' },
  { value: 'DistanceLearning', label: 'Distance Learning' },
  { value: 'ShiftManagement', label: 'Shift Management' },
  { value: 'FieldNotifications', label: 'Field Notifications' },
  { value: 'DatingSocial', label: 'Dating/Social' },
  { value: 'RemoteAppointments', label: 'Remote appointments' },
  { value: 'GroupMessaging', label: 'Group Messaging' },
  { value: 'ExamProctoring', label: 'Exam Proctoring' },
  { value: 'Tutoring', label: 'Tutoring' },
  { value: 'TherapyIndividualGroup', label: 'Therapy (Individual+Group)' },
  { value: 'Pharmacy', label: 'Pharmacy' },
  { value: 'FirstResponder', label: 'First Responder' },
  { value: 'SurveyResearch', label: 'Survey/Research' },
]

// ── Verification issue field-key mapping ───────────────────────────────────
// Maps a rejected-issue field_key back to the wizard field + step so it can be
// shown inline in the read-only review. Includes both the raw Twilio field ids
// (business_name, first_name, …) and our own form keys so it resolves whatever
// the backend sends.

export interface FieldLocation {
  formKey: string
  step: number
}

export const VERIFICATION_FIELD_KEY_MAP: Record<string, FieldLocation> = {
  // Twilio field ids (dialer_verification.md §5)
  business_name: { formKey: 'legalBusinessName', step: 2 },
  business_type: { formKey: 'businessType', step: 2 },
  business_industry: { formKey: 'industry', step: 2 },
  business_registration_identifier: { formKey: 'registrationIdentifierType', step: 2 },
  business_registration_number: { formKey: 'registrationNumber', step: 2 },
  business_regions_of_operation: { formKey: 'regionOfOperation', step: 2 },
  website_url: { formKey: 'websiteUrl', step: 2 },
  first_name: { formKey: 'firstName', step: 4 },
  last_name: { formKey: 'lastName', step: 4 },
  email: { formKey: 'email', step: 4 },
  phone_number: { formKey: 'phoneNumber', step: 4 },
  job_position: { formKey: 'jobPosition', step: 4 },
  // Our own form keys (defensive — if the backend already rephrased to them)
  businessName: { formKey: 'businessName', step: 1 },
  legalBusinessName: { formKey: 'legalBusinessName', step: 2 },
  businessIdentity: { formKey: 'businessIdentity', step: 2 },
  businessType: { formKey: 'businessType', step: 2 },
  industry: { formKey: 'industry', step: 2 },
  registrationIdentifierType: { formKey: 'registrationIdentifierType', step: 2 },
  registrationNumber: { formKey: 'registrationNumber', step: 2 },
  regionOfOperation: { formKey: 'regionOfOperation', step: 2 },
  websiteUrl: { formKey: 'websiteUrl', step: 2 },
  socialMediaProfileUrl: { formKey: 'socialMediaProfileUrl', step: 2 },
  customerName: { formKey: 'customerName', step: 3 },
  street: { formKey: 'street', step: 3 },
  streetSecondary: { formKey: 'streetSecondary', step: 3 },
  city: { formKey: 'city', step: 3 },
  region: { formKey: 'region', step: 3 },
  postalCode: { formKey: 'postalCode', step: 3 },
  isoCountry: { formKey: 'isoCountry', step: 3 },
  firstName: { formKey: 'firstName', step: 4 },
  lastName: { formKey: 'lastName', step: 4 },
  businessTitle: { formKey: 'businessTitle', step: 4 },
  jobPosition: { formKey: 'jobPosition', step: 4 },
}

// ── Guardrails defaults + weekday / time helpers ──────────────────────────

export const WEEKDAYS: Array<{ key: string; letter: string; full: string }> = [
  { key: 'mon', letter: 'M', full: 'Monday' },
  { key: 'tue', letter: 'T', full: 'Tuesday' },
  { key: 'wed', letter: 'W', full: 'Wednesday' },
  { key: 'thu', letter: 'T', full: 'Thursday' },
  { key: 'fri', letter: 'F', full: 'Friday' },
  { key: 'sat', letter: 'S', full: 'Saturday' },
  { key: 'sun', letter: 'S', full: 'Sunday' },
]

export interface GuardrailsShape {
  days: string[]
  start: string // "HH:MM" 24h
  end: string
  recordingEnabled: boolean
  excludedRegions: string[] // prefixed ISO-3166-2
}

// Defaults pre-filled in the guardrails form (design 1f): weekdays 8:00–18:00,
// recording on, no exclusions. Mirrors the server's DEFAULT_CALLING_HOURS.
export const DEFAULT_GUARDRAILS: GuardrailsShape = {
  days: ['mon', 'tue', 'wed', 'thu', 'fri'],
  start: '08:00',
  end: '18:00',
  recordingEnabled: true,
  excludedRegions: [],
}

// 30-minute time options for the calling-hours selects: value "HH:MM" (24h),
// label "8:00 AM".
export const TIME_OPTIONS: Option[] = (() => {
  const opts: Option[] = []
  for (let m = 0; m < 24 * 60; m += 30) {
    const h24 = Math.floor(m / 60)
    const min = m % 60
    const value = `${String(h24).padStart(2, '0')}:${String(min).padStart(2, '0')}`
    const ampm = h24 < 12 ? 'AM' : 'PM'
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12
    opts.push({ value, label: `${h12}:${String(min).padStart(2, '0')} ${ampm}` })
  }
  return opts
})()

// "08:00" → "8:00 AM"; falls back to the raw value.
export function formatTimeLabel(value: string): string {
  return TIME_OPTIONS.find((o) => o.value === value)?.label || value
}

// A short "Weekdays · 8:00 AM – 6:00 PM" style summary of a calling-hours set.
export function summarizeDays(days: string[]): string {
  const set = new Set(days)
  const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri']
  const isWeekdays = weekdayKeys.every((k) => set.has(k)) && !set.has('sat') && !set.has('sun')
  if (isWeekdays) return 'Weekdays'
  if (set.size === 7) return 'Every day'
  if (set.size === 0) return 'No days selected'
  return WEEKDAYS.filter((d) => set.has(d.key))
    .map((d) => d.full.slice(0, 3))
    .join(', ')
}
